import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import { CreateGerminationRoomData, UpdateGerminationRoomData } from 'api/germinationRoom';
import {
  PaginationData,
  GerminationRoom,
} from 'constants/interface';
import { RootState } from 'state';

const germinationRoomEntityAdapter = createEntityAdapter<GerminationRoom>();

export interface GerminationRoomsState extends EntityState<GerminationRoom> {
  isLoading: boolean
  remoteTotal: number
}

export const initialState: GerminationRoomsState = germinationRoomEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
});

export const createGerminationRoomAction = createAsyncThunk(
  'germination-rooms/create',
  async (payload: CreateGerminationRoomData) => {
    const germinationRoomData = await apiClient.germinationRoom.createGerminationRoom(payload);
    return germinationRoomData;
  },
);

export const updateGerminationRoomAction = createAsyncThunk(
  'germination-rooms/update',
  async (payload: UpdateGerminationRoomData) => {
    const germinationRoomData = await apiClient.germinationRoom.updateGerminationRoom(payload);
    return germinationRoomData;
  },
);

export const getGerminationRoomAction = createAsyncThunk(
  'germination-rooms/get',
  async (payload: {
    germinationRoomId: string,
  }) => {
    const { germinationRoomId } = payload;
    const response = await apiClient.germinationRoom.getGerminationRoom(germinationRoomId);

    return response;
  },
);

export const getGerminationRoomListAction = createAsyncThunk(
  'germination-rooms/list',
  async (payload: PaginationData) => {
    const response = await apiClient
      .germinationRoom
      .getGerminationRoomList(payload);

    return response;
  },
);

export const germinationRoomsSlice = createSlice({
  name: 'germination-rooms',
  initialState,
  reducers: {
    cleanGerminationRoomsAction: germinationRoomEntityAdapter.removeAll,
  },
  extraReducers: (builder) => {
    // List
    builder.addCase(getGerminationRoomListAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getGerminationRoomListAction.fulfilled, (state, action) => {
      germinationRoomEntityAdapter.removeAll(state);
      germinationRoomEntityAdapter.addMany(state, action.payload.data);
      state.remoteTotal = action.payload.pagination.total;
      state.isLoading = false;
    });
    builder.addCase(getGerminationRoomListAction.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  cleanGerminationRoomsAction,
} = germinationRoomsSlice.actions;

// Selectors

export const isLoadingGerminationRoomsSelector = (state: RootState):
  boolean => state.germinationRooms.isLoading;

export const germinationRoomsEntitiesSelector = (state: RootState):
  Dictionary<GerminationRoom> => state.germinationRooms.entities;

export const germinationRoomsIdsSelector = (state: RootState):
  EntityId[] => state.germinationRooms.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.germinationRooms.remoteTotal;
